import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner, faExclamationCircle, faArrowLeft } from '@fortawesome/free-solid-svg-icons';
import { useAuth } from '../contexts/AuthContext';
import Logo from '../components/layout/Logo';

export default function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      const user = await login(email, password);
      if (user) {
        // Route by role: 2 = Admin, 1 = Instructor, 0 = Student
        if (user.role === 2) {
          navigate('/admin/dashboard');
        } else if (user.role === 1) {
          navigate('/instructor/dashboard');
        } else if (user.role === 3) {
          navigate('/moderator/dashboard');
        } else {
          navigate('/dashboard');
        }
      } else {
        setError('Invalid email or password. Please try again.');
      }
    } catch (err) {
      setError('An error occurred. Please try again.');
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-brand-50 to-canvas-100 flex items-center justify-center px-4">
      <div className="w-full max-w-md">
        {/* Back Button */}
        <button
          onClick={() => navigate('/')}
          className="mb-6 flex items-center gap-2 text-sm font-semibold text-brand-700 hover:text-brand-800 transition-colors"
        >
          <FontAwesomeIcon icon={faArrowLeft} className="h-4 w-4" />
          Back to home
        </button>

        {/* Logo */}
        <div className="text-center mb-8">
          <Logo variant="dark" />
          <h1 className="mt-6 text-2xl font-bold text-brand-950">
            Sign in to your account
          </h1>
          <p className="mt-2 text-sm text-brand-900/60">
            Access your learnership dashboard and course materials
          </p>
        </div>

        {/* Login Card */}
        <div className="bg-white rounded-3xl shadow-card p-8 border border-canvas-200">
          <form onSubmit={handleSubmit} className="space-y-5">
            {/* Email */}
            <div>
              <label
                htmlFor="email"
                className="block text-sm font-semibold text-brand-950 mb-2"
              >
                Email address
              </label>
              <input
                id="email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full px-4 py-3 border border-canvas-300 rounded-2xl text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent transition-all"
                placeholder="you@example.com"
                disabled={isLoading}
              />
            </div>

            {/* Password */}
            <div>
              <label
                htmlFor="password"
                className="block text-sm font-semibold text-brand-950 mb-2"
              >
                Password
              </label>
              <input
                id="password"
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-4 py-3 border border-canvas-300 rounded-2xl text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent transition-all"
                placeholder="••••••••"
                disabled={isLoading}
              />
            </div>

            {/* Error Message */}
            {error && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-2xl flex items-center gap-2">
                <FontAwesomeIcon icon={faExclamationCircle} className="h-4 w-4 text-red-700 flex-shrink-0" />
                <p className="text-sm text-red-700">{error}</p>
              </div>
            )}

            {/* Submit Button */}
            <button
              type="submit"
              disabled={isLoading}
              className="w-full bg-brand-gradient text-white font-semibold py-3 rounded-2xl transition-all duration-200 hover:shadow-lg disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {isLoading ? (
                <span className="flex items-center justify-center gap-2">
                  <FontAwesomeIcon icon={faSpinner} className="h-4 w-4 animate-spin" />
                  Signing in...
                </span>
              ) : (
                'Sign in'
              )}
            </button>
          </form>

          {/* Demo Credentials */}
          <div className="mt-6 pt-6 border-t border-canvas-200">
            <p className="text-xs text-brand-900/50 text-center mb-3">
              Demo credentials (to be created via admin panel):
            </p>
            <p className="text-xs text-brand-900/60 text-center">
              Email: <span className="font-mono">student@example.com</span>
              <br />
              Password: <span className="font-mono">securepass123</span>
            </p>
          </div>
        </div>

        {/* Footer */}
        <p className="text-center text-xs text-brand-900/50 mt-6">
          Having trouble signing in?{' '}
          <a href="/contact" className="text-brand-700 hover:text-brand-800 font-semibold">
            Contact support
          </a>
        </p>
      </div>
    </div>
  );
}
